import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Store as StoreIcon, ShieldCheck, ShoppingBag } from 'lucide-react';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import LibraryCard from '@/components/store/LibraryCard';
import { useAddonFamilies, type LibraryAddon } from '@/hooks/useLibraryAddons';
import { useCreateAddonCheckout } from '@/hooks/useCreateAddonCheckout';
import { toast } from 'sonner';

const BRAND_SECTIONS: Array<{ brand: string; label: string; subtitle: string }> = [
  { brand: 'saint_gobain', label: 'Saint-Gobain', subtitle: 'Catalogo Habito, Gyproc, Isover, Megastil' },
  { brand: 'knauf', label: 'Knauf', subtitle: 'Catalogo Diamant, Vidiwall, Cleaneo, Insulation' },
  { brand: 'fassa', label: 'Fassa Bortolo', subtitle: 'Sistema Gypsotech: lastre, profili, premiscelati' },
  { brand: 'rproduzioni', label: 'RProduzioni', subtitle: 'Profili metallici: guide, montanti, angolari, spalla alta (acciaio zincato e zinco-magnesio)' },
];

export default function Store() {
  const { isLoading, families } = useAddonFamilies();
  const { createAddonCheckout } = useCreateAddonCheckout();
  const [purchasingId, setPurchasingId] = React.useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.get('purchase') === 'success') {
      toast.success('Acquisto completato', {
        description: 'Il pacchetto è ora disponibile nel tuo catalogo. Materiali e stratigrafie compaiono entro pochi secondi.',
        duration: 8000,
      });
      queryClient.invalidateQueries({ queryKey: ['org_entitlements'] });
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      queryClient.invalidateQueries({ queryKey: ['unified-stratigraphies'] });
      searchParams.delete('purchase');
      searchParams.delete('addon');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  const handlePurchase = async (addon: LibraryAddon) => {
    setPurchasingId(addon.id);
    try {
      await createAddonCheckout(addon.id);
    } finally {
      setPurchasingId(null);
    }
  };

  // F29.3: per ogni brand voglio { materials, stratigraphies, bundle } come FAMIGLIE
  const sections = BRAND_SECTIONS.map((sec) => {
    const ofBrand = families.filter((f) => f.brand_slug === sec.brand);
    return {
      ...sec,
      materials: ofBrand.find((f) => f.category === 'brand'),
      stratigraphies: ofBrand.find((f) => f.category === 'stratigraphies'),
      bundle: ofBrand.find((f) => f.category === 'bundle'),
    };
  });

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <Header />
        <div className="p-6 max-w-6xl mx-auto w-full">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <StoreIcon className="h-7 w-7 text-primary" />
                <h1 className="text-2xl font-bold tracking-tight">Store librerie</h1>
              </div>
              <p className="text-zinc-600 max-w-2xl">
                Espandi DryConfig acquistando i listini prezzi e le stratigrafie certificate dei principali produttori. Per ogni brand scegli listino, stratigrafie o bundle scontato — e l'annata che preferisci dal menu versione.
              </p>
            </div>
            <Button asChild variant="outline" className="shrink-0">
              <Link to="/store/my-purchases">
                <ShoppingBag className="h-4 w-4 mr-2" />
                I miei acquisti
              </Link>
            </Button>
          </div>

          <Card className="mb-6 bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="p-4 flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <strong className="text-blue-900">La tua libreria base include già 50 materiali e 5 stratigrafie demo</strong>
                <p className="text-blue-700 mt-1">
                  Sono i prodotti generici starter (lastre, profili, isolanti, viti, stucchi) presenti in ogni DryConfig. I prodotti qui sotto aggiungono i cataloghi brand specifici con prezzi e certificazioni.
                </p>
              </div>
            </CardContent>
          </Card>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
            </div>
          ) : (
            <div className="space-y-10">
              {sections.map((sec) => {
                const allItems = [sec.materials, sec.stratigraphies, sec.bundle].filter(Boolean);
                if (allItems.length === 0) return null;
                return (
                  <section key={sec.brand}>
                    <div className="mb-3">
                      <h2 className="text-lg font-semibold text-zinc-900">{sec.label}</h2>
                      <p className="text-sm text-zinc-500">{sec.subtitle}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sec.materials && (
                        <LibraryCard
                          versions={sec.materials.versions}
                          defaultVersion={sec.materials.defaultVersion}
                          onPurchase={handlePurchase}
                          isPurchasing={purchasingId === sec.materials.defaultVersion.id}
                        />
                      )}
                      {sec.stratigraphies && (
                        <LibraryCard
                          versions={sec.stratigraphies.versions}
                          defaultVersion={sec.stratigraphies.defaultVersion}
                          onPurchase={handlePurchase}
                          isPurchasing={purchasingId === sec.stratigraphies.defaultVersion.id}
                        />
                      )}
                      {sec.bundle && (
                        <LibraryCard
                          versions={sec.bundle.versions}
                          defaultVersion={sec.bundle.defaultVersion}
                          onPurchase={handlePurchase}
                          isPurchasing={purchasingId === sec.bundle.defaultVersion.id}
                          isBundle
                          bundleSavings={
                            sec.materials && sec.stratigraphies
                              ? Math.max(0, Number(sec.materials.defaultVersion.price_eur) + Number(sec.stratigraphies.defaultVersion.price_eur) - Number(sec.bundle.defaultVersion.price_eur))
                              : 0
                          }
                        />
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
